import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { post, errMsg, inr, PRODUCT } from "@/lib/api";
import { useAuth } from "@/context/Auth";
import { Field, Btn, Logo } from "@/components/kit";
import { OtpLogin } from "@/pages/Login";

const STATES = ["Gujarat", "Maharashtra", "Rajasthan", "Madhya Pradesh", "Karnataka", "Delhi", "Tamil Nadu", "Telangana", "Uttar Pradesh", "West Bengal", "Other"];
const CONSENTS = [["KYC", true], ["CREDIT_BUREAU", true], ["LENDER_SHARING", true], ["PRIVACY_POLICY", true], ["TERMS", true], ["COMMUNICATION", false], ["MARKETING", false]];
const TEXT = {
  KYC: "I authorise FinOrbit Finance Pvt Ltd (LSP) to verify my KYC details with authorised providers.",
  CREDIT_BUREAU: "I authorise FinOrbit and partner lenders to fetch my credit report for loan evaluation.",
  LENDER_SHARING: "I consent to share my application and documents with partner lenders. The lender takes the final credit decision.",
  PRIVACY_POLICY: "I have read and accept the Privacy Policy.", TERMS: "I accept the Terms & Conditions.",
  COMMUNICATION: "I agree to receive calls, SMS, WhatsApp and email about my application.", MARKETING: "I agree to receive marketing offers.",
};

function Basic({ P, product, staff }) {
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <Field label={product === "BL" ? "Applicant name" : "Full name (as per PAN)"} name="full_name" required {...P} />
      {staff && <Field label="Customer mobile" name="mobile" required {...P} />}
      <Field label="Email" name="email" type="email" {...P} />
      <Field label="PAN" name="pan" required placeholder="ABCDE1234F" {...P} />
      {product !== "BL" && <Field label="Date of birth" name="dob" type="date" {...P} />}
      {product !== "BL" && <Field label="Gender" name="gender" options={["MALE", "FEMALE", "OTHER"]} {...P} />}
      <Field label="Aadhaar (last 4 digits only)" name="aadhaar_last4" hint="Full Aadhaar is never stored" {...P} />
      <Field label="City" name="city" required {...P} />
      <Field label="State" name="state" options={STATES} required {...P} />
      <Field label="Pincode" name="pincode" {...P} />
      {product !== "BL" && <Field label="Residence type" name="residence_type" options={["OWNED", "RENTED", "PARENTAL", "COMPANY_PROVIDED"]} {...P} />}
    </div>
  );
}

function Loan({ P, form }) {
  const r = 14 / 1200, n = Number(form.tenure_months || 0), p = Number(form.loan_amount || 0);
  const emi = n && p ? Math.round((p * r * (1 + r) ** n) / ((1 + r) ** n - 1)) : 0;
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <Field label="Loan amount (₹)" name="loan_amount" type="number" required {...P} />
      <Field label="Tenure (months)" name="tenure_months" options={[12, 18, 24, 36, 48, 60, 72, 84, 120, 180, 240, 300, 360].map((x) => [x, `${x} months`])} required {...P} />
      <div className="rounded-xl bg-slate-50 p-4 md:col-span-2"><p className="text-xs text-slate-500">Indicative EMI @14% (illustrative only)</p><p className="font-display text-2xl font-bold text-blue-900" data-testid="wizard-emi">{inr(emi)}</p></div>
    </div>
  );
}

function Work({ P, product }) {
  if (product === "BL") return (
    <div className="grid gap-4 md:grid-cols-2">
      <Field label="Business name" name="business_name" required {...P} />
      <Field label="Business PAN" name="business_pan" {...P} />
      <Field label="GSTIN" name="gstin" {...P} />
      <Field label="Business type" name="business_type" options={["PROPRIETORSHIP", "PARTNERSHIP", "PRIVATE_LIMITED", "LLP", "OTHER"]} required {...P} />
      <Field label="Business vintage (years)" name="vintage_years" type="number" required {...P} />
      <Field label="Annual turnover (₹)" name="annual_turnover" type="number" required {...P} />
      <Field label="Monthly banking credits (₹)" name="monthly_banking" type="number" {...P} />
      <Field label="Existing loans (count)" name="existing_loans" type="number" {...P} />
      <Field label="Existing EMI (₹/month)" name="existing_emi" type="number" {...P} />
      <Field label="Business address" name="business_address" className="md:col-span-2" {...P} />
      <p className="text-xs text-slate-500 md:col-span-2">Bank statements, GST and ITR documents can be uploaded on the next screen.</p>
    </div>
  );
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <Field label="Employment type" name="employment_type" options={["SALARIED", "SELF_EMPLOYED", "PROFESSIONAL"]} required {...P} />
      <Field label="Company / business name" name="company_name" {...P} />
      <Field label="Work experience (years)" name="work_experience_years" type="number" {...P} />
      <Field label="Net monthly income (₹)" name="monthly_income" type="number" required {...P} />
      <Field label="Existing EMI (₹/month)" name="existing_emi" type="number" {...P} />
      <Field label="Salary bank account no." name="bank_account_no" {...P} />
      <Field label="IFSC" name="bank_ifsc" {...P} />
      {product === "HL" && <>
        <h4 className="pt-2 text-xs font-bold uppercase tracking-wider text-slate-400 md:col-span-2">Property details</h4>
        <Field label="Loan purpose" name="loan_purpose" options={["PURCHASE", "CONSTRUCTION", "RENOVATION", "BALANCE_TRANSFER", "PLOT_PURCHASE"]} required {...P} />
        <Field label="Property type" name="property_type" options={["APARTMENT", "INDEPENDENT_HOUSE", "VILLA", "PLOT", "UNDER_CONSTRUCTION"]} required {...P} />
        <Field label="Property value (₹)" name="property_value" type="number" required hint="Loan is typically up to 80% of property value" {...P} />
        <Field label="Property city" name="property_city" {...P} />
      </>}
      {product === "CL" && <>
        <h4 className="pt-2 text-xs font-bold uppercase tracking-wider text-slate-400 md:col-span-2">Vehicle details</h4>
        <Field label="Car type" name="vehicle_type" options={["NEW", "USED"]} required {...P} />
        <Field label="Make & model" name="vehicle_make_model" required placeholder="e.g. Maruti Suzuki Brezza ZXi" {...P} />
        <Field label="On-road price (₹)" name="vehicle_price" type="number" required hint="Loan is typically up to 90% of on-road price" {...P} />
        <Field label="Down payment (₹)" name="down_payment" type="number" {...P} />
        <Field label="Dealer name" name="dealer_name" {...P} />
      </>}
    </div>
  );
}

function Consent({ form, setForm }) {
  const c = form.consents || [];
  const toggle = (t) => setForm({ ...form, consents: c.includes(t) ? c.filter((x) => x !== t) : [...c, t] });
  return (
    <div className="space-y-3">
      {CONSENTS.map(([t, req]) => (
        <label key={t} className="flex items-start gap-3 rounded-lg border border-slate-200 p-3 text-sm">
          <input data-testid={`consent-${t}`} type="checkbox" checked={c.includes(t)} onChange={() => toggle(t)} className="mt-1" />
          <span>{TEXT[t]} {req && <span className="text-rose-500">*</span>}</span>
        </label>
      ))}
      <button type="button" data-testid="consent-accept-all" onClick={() => setForm({ ...form, consents: CONSENTS.map((x) => x[0]) })} className="text-xs font-semibold text-blue-800">Accept all</button>
      <p className="text-[11px] text-slate-400">Consent version v1.0. Your IP, device and timestamp are recorded for compliance. FinOrbit is an LSP and not a lender.</p>
    </div>
  );
}

const REQ = {
  0: { PL: ["full_name", "pan", "city", "state"], BL: ["full_name", "pan", "city", "state"] },
  1: { PL: ["loan_amount", "tenure_months"], BL: ["loan_amount", "tenure_months"] },
  2: { PL: ["employment_type", "monthly_income"], BL: ["business_name", "business_type", "vintage_years", "annual_turnover"],
       HL: ["employment_type", "monthly_income", "loan_purpose", "property_type", "property_value"], CL: ["employment_type", "monthly_income", "vehicle_type", "vehicle_make_model", "vehicle_price"] },
};
const STEPS = ["Basic details", "Loan requirement", "Employment / Business / Asset", "Consent"];

export default function Apply({ staff }) {
  const { product: p0 } = useParams();
  const { user, setUser } = useAuth();
  const nav = useNavigate();
  const [product, setProduct] = useState(!staff && PRODUCT[p0] ? p0 : "PL");
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ existing_emi: 0, consents: [] });
  const [busy, setBusy] = useState(false);
  const isCustomer = user && user.role === "customer";
  const P = { form, setForm };
  useEffect(() => {
    if (isCustomer) post("/journey/track", { step: STEPS[step] || "OTP_VERIFIED", product }).catch(() => {});
  }, [step, product, isCustomer]);
  const next = () => {
    const fields = REQ[step]?.[product] || REQ[step]?.PL || [];
    const miss = fields.concat(staff && step === 0 ? ["mobile"] : []).filter((k) => form[k] === undefined || form[k] === "");
    if (miss.length) return toast.error(`Please fill: ${miss.join(", ").replace(/_/g, " ")}`);
    setStep(step + 1);
  };
  const submit = async () => {
    const need = CONSENTS.filter((c) => c[1]).map((c) => c[0]).filter((c) => !form.consents.includes(c));
    if (need.length) return toast.error("Please accept the required consents");
    setBusy(true);
    try {
      const body = { ...form, product, mobile: staff ? form.mobile : user.mobile, source: staff ? undefined : "WEBSITE" };
      Object.keys(body).forEach((k) => body[k] === "" && delete body[k]);
      const r = await post("/applications", body, { headers: { "Idempotency-Key": `app-${user.id}-${product}-${form.pan}-${form.loan_amount}` } });
      toast.success(`Application ${r.data.app_code} created`);
      nav(staff ? `/crm/applications/${r.data.id}` : `/portal/applications/${r.data.id}`);
    } catch (e) {
      const dups = e?.response?.data?.data?.duplicates;
      toast.error(errMsg(e) + (dups ? `: ${dups.map((d) => `${d.lead_code} (${d.status})`).join(", ")}` : ""));
      if (e?.response?.data?.error_code === "APP_EXISTS") nav(`/portal/applications/${e.response.data.data.id}`);
    }
    setBusy(false);
  };
  const wizard = (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 md:p-8" data-testid="apply-wizard">
      <div className="mb-6 flex flex-wrap items-center gap-2">
        {Object.keys(PRODUCT).map((x) => (
          <button key={x} data-testid={`product-${x}`} onClick={() => { setProduct(x); setStep(0); }} className={`rounded-full border px-4 py-1.5 text-sm font-semibold ${product === x ? "border-blue-900 bg-blue-900 text-white" : "border-slate-300"}`}>{PRODUCT[x]}</button>
        ))}
      </div>
      <div className="mb-6 grid grid-cols-4 gap-2">
        {STEPS.map((s, i) => (
          <div key={s}>
            <div className={`h-1.5 rounded-full ${i <= step ? "bg-blue-900" : "bg-slate-200"}`} />
            <p className={`mt-1.5 hidden text-[11px] font-semibold sm:block ${i === step ? "text-blue-900" : "text-slate-400"}`}>{i < step && <Check className="inline h-3 w-3" />} {s}</p>
          </div>
        ))}
      </div>
      <h2 className="mb-4 font-display text-lg font-semibold" data-testid="wizard-step-title">{STEPS[step]}</h2>
      {step === 0 && <Basic P={P} product={product} staff={staff} />}
      {step === 1 && <Loan P={P} form={form} />}
      {step === 2 && <Work P={P} product={product} />}
      {step === 3 && <Consent form={form} setForm={setForm} />}
      <div className="mt-8 flex justify-between">
        <Btn variant="outline" data-testid="wizard-back-btn" disabled={step === 0} onClick={() => setStep(step - 1)}>Back</Btn>
        {step < 3 ? <Btn data-testid="wizard-next-btn" onClick={next}>Continue</Btn> : <Btn data-testid="wizard-submit-btn" variant="success" loading={busy} onClick={submit}>Submit application</Btn>}
      </div>
    </div>
  );
  if (staff) return <div className="mx-auto max-w-3xl"><h1 className="mb-1 font-display text-2xl font-bold">New Application</h1><p className="mb-6 text-sm text-slate-500">Create a customer and application. Duplicate checks run automatically.</p>{wizard}</div>;
  return (
    <div className="min-h-screen bg-slate-50 px-4 py-6 md:py-10">
      <div className="mx-auto max-w-3xl">
        <div className="mb-6 flex items-center justify-between"><Link to="/"><Logo /></Link>{user && <Link to={user.role === "customer" ? "/portal" : "/crm"} className="text-sm font-semibold text-blue-800">Dashboard</Link>}</div>
        {user === null ? null : !user ? (
          <div className="mx-auto max-w-md rounded-2xl border border-slate-200 bg-white p-6">
            <h2 className="font-display text-xl font-bold">Apply for {PRODUCT[product]}</h2>
            <p className="mb-4 text-sm text-slate-500">Start with your mobile number - we'll verify it with an OTP.</p>
            <OtpLogin portal="customer" onDone={(u) => setUser(u)} />
          </div>
        ) : !isCustomer ? (
          <div className="rounded-2xl border bg-white p-6 text-sm">You are logged in as staff/partner. <Link className="font-semibold text-blue-800" to="/crm/applications/new">Create an application from CRM →</Link></div>
        ) : wizard}
      </div>
    </div>
  );
}
